// Copyright (c) 2026 杨翔宇（武汉文理学院）
// 物理智能教学智能体平台 (Physics Intelligent Agent)
// 保留所有权利 (All Rights Reserved)。未经著作权人书面许可，
// 不得以任何形式复制、传播、修改或用于商业用途。

import { useEffect } from 'react';

import { shouldMountCursor } from '@/rendering/appSurfacePolicy';
import { useAppSurfacePolicy } from '@/rendering/useAppSurfacePolicy';

/** vendor `mountCursor()` 的公开返回（T06 补齐幂等 dispose；此处按可选声明以兼容声明文件更新顺序）。 */
interface LatticeCursorHandle {
  el: HTMLCanvasElement | null;
  dispose?: () => void;
}

/**
 * 眼 CursorHalo（Lattice 全局件）：光标点集在可操作控件轮廓上重组，按下时炸开，
 * 键盘焦点也用同一圈点做指示。库内部已跳过触摸设备与 prefers-reduced-motion。
 * 依赖 CSS 变量 --ink / --hot 取色（index.css 按明暗主题提供）。
 *
 * 批次性能优化 T03：智能体工作区**不挂载**粒子光标——按页面渲染策略直接跳过动态
 * import，而不是挂一个隐藏的 Canvas（隐藏不等于停止渲染）。首页/登录/认证页保持
 * 现有效果。existing→workspace 切换时通过 vendor 公开的 dispose 完整回收
 * 时钟订阅、window/document 监听器、MutationObserver 与 Canvas；异步 import 尚未
 * 完成就切路由时丢弃挂载并清理。
 */
export default function LatticeCursorHalo() {
  const policy = useAppSurfacePolicy();
  const enabled = shouldMountCursor(policy);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    let disposed = false;
    let handle: LatticeCursorHandle | null = null;

    const release = (mounted: LatticeCursorHandle | null) => {
      if (!mounted) return;
      // 幂等 dispose：时钟订阅 + window/document 监听器 + MutationObserver + Canvas
      mounted.dispose?.();
      // 兼容未提供 dispose 的旧声明（T06 之前）：至少移除节点与类，不留下可见残留
      mounted.el?.remove();
      document.documentElement.classList.remove('has-cursor');
    };

    void (async () => {
      try {
        const { mountCursor } = await import('@/vendor/lattice/dom/cursor.js');
        // 异步 import 期间可能已经切走（改用系统鼠标/降级）：丢弃挂载。
        if (disposed) return;
        const mounted = mountCursor() as LatticeCursorHandle;
        handle = mounted;
        // 极端竞态：挂载返回的同一 tick 内被清理（StrictMode 双执行）。
        if (disposed) {
          handle = null;
          release(mounted);
        }
      } catch {
        // 触摸设备 / 减弱动态 / 无 canvas：库内部已静默跳过
      }
    })();

    return () => {
      disposed = true;
      const mounted = handle;
      handle = null;
      release(mounted);
    };
  }, [enabled]);

  return null;
}
